import { Plus, Trash2, Info } from 'lucide-react';
import type { TemplateEditorState } from '../templateEditor/types';
import { isGeocodingApplicable } from '../templateEditor/types';
import { ADDRESS_FIELD_OPTIONS } from '../utils/targetFieldOptions';

export interface GeocodingTabContentProps {
  state: TemplateEditorState;
  onChange: (patch: Partial<TemplateEditorState>) => void;
  errors?: string[];
}

export default function GeocodingTabContent({ state, onChange, errors = [] }: GeocodingTabContentProps) {
  // Item 2 (2026-05-08): defensive non-applicable surface. The wizard's
  // tab visibility (getVisibleEditorTabs) already strips this tab for
  // parcel + lookup imports, so under normal navigation the user never
  // reaches this branch. We render a clear explanation + disable the
  // toggle here as a belt-and-braces guard against direct-URL hits or
  // host code that bypasses the visibility helper.
  const geocodingApplies = isGeocodingApplicable(state.dataMode);
  if (!geocodingApplies) {
    return (
      <div className="space-y-5 max-w-xl" data-testid="geocoding-not-applicable">
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-2">
          <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
          <div className="text-xs text-blue-800 space-y-1">
            <p className="font-medium">Geocoding does not apply to {state.dataMode} imports.</p>
            <p>
              {state.dataMode === 'parcel'
                ? 'Parcel rows already supply boundary geometry; coordinates are derived from the polygon centroid, so an address-to-point pass would never run.'
                : 'Lookup rows are not spatial — they have no address columns to geocode.'}
              {' '}Switch the data mode on the Basics tab if you need geocoding.
            </p>
          </div>
        </div>
        <div className="border border-gray-200 rounded-lg p-4 space-y-3 opacity-60">
          <div className="text-xs font-semibold text-gray-700">Geocoding</div>
          <label className="flex items-center gap-2 select-none cursor-not-allowed">
            <input
              type="checkbox"
              data-testid="geocoding-toggle"
              className="w-4 h-4"
              checked={false}
              disabled
              readOnly
            />
            <span className="text-sm text-gray-500">Enable geocoding for this template (not available for {state.dataMode})</span>
          </label>
        </div>
      </div>
    );
  }
  const addAddressField = () => {
    onChange({ geocodingAddressFields: [...(state.geocodingAddressFields ?? []), ''] });
  };

  const updateAddressField = (i: number, val: string) => {
    const fields = [...(state.geocodingAddressFields ?? [])];
    fields[i] = val;
    onChange({ geocodingAddressFields: fields });
  };

  const removeAddressField = (i: number) => {
    onChange({
      geocodingAddressFields: (state.geocodingAddressFields ?? []).filter((_, idx) => idx !== i),
    });
  };

  return (
    <div className="space-y-5 max-w-xl">
      {errors.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 space-y-1">
          {errors.map(e => (
            <p key={e} className="text-xs text-red-700">• {e}</p>
          ))}
        </div>
      )}

      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="text-xs font-semibold text-gray-700">Geocoding</div>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            data-testid="geocoding-toggle"
            className="w-4 h-4"
            checked={!!state.geocodingEnabled}
            onChange={e => onChange({ geocodingEnabled: e.target.checked })}
          />
          <span className="text-sm text-gray-700">Enable geocoding for this template</span>
        </label>

        {state.geocodingEnabled && (
          <div className="space-y-3 pt-1">
            <p className="text-xs text-gray-500">
              Select which mapped target fields form the address used for geocoding.
              Only fields mapped in <strong>Field Mappings</strong> are eligible.
            </p>

            <div className="space-y-2">
              {(state.geocodingAddressFields ?? []).length === 0 ? (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
                  Add at least one address field below.
                </p>
              ) : (
                (state.geocodingAddressFields ?? []).map((field, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <select
                      data-testid={`geocoding-field-${i}`}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-hidden focus:ring-1 focus:ring-blue-500"
                      value={field}
                      onChange={e => updateAddressField(i, e.target.value)}
                    >
                      <option value="">— Select address field —</option>
                      {state.fieldMappings.length > 0 && (
                        <optgroup label="From your field mappings">
                          {state.fieldMappings.map(m => (
                            <option key={m.targetField} value={m.targetField}>{m.targetField}</option>
                          ))}
                        </optgroup>
                      )}
                      <optgroup label="Common address fields">
                        {ADDRESS_FIELD_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label} ({o.value})</option>
                        ))}
                      </optgroup>
                    </select>
                    <button
                      type="button"
                      onClick={() => removeAddressField(i)}
                      className="p-1 text-red-400 hover:text-red-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))
              )}
            </div>

            <button
              type="button"
              onClick={addAddressField}
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
            >
              <Plus className="h-3.5 w-3.5" />
              Add address field
            </button>
          </div>
        )}
      </div>

      <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
        <Info className="h-4 w-4 shrink-0 text-gray-400 mt-0.5" />
        <div>
          Geocoding runs server-side via Azure Maps after each import commit.
          Only entities missing coordinates are geocoded.
          Files that already contain geometry (GeoPackage, GeoJSON, Shapefile) do not require geocoding.
        </div>
      </div>
    </div>
  );
}
